import * as React from 'react';

import {TimelineComments} from 'shells/timeline/entry/comments/timeline-comments';
import type {Props as TimelineCommentsProps} from 'shells/timeline/entry/comments/timeline-comments';
import {TimelineEntryFooter} from 'shells/timeline/entry/email-engagement-reaction-bar/timeline-entry-footer';
import type {Props as TimelineEntryFooterProps} from 'shells/timeline/entry/email-engagement-reaction-bar/timeline-entry-footer';
import type {Props as TimelineHeaderProps} from 'shells/timeline/entry/header/timeline-header';
import type {ToolbarOption} from 'shells/timeline/entry/toolbar/timeline-entry-toolbar';
import type {TimelineEntryIconVariant} from 'shells/timeline/gutter/types';
import type {Reaction, Reactions} from 'shells/timeline/reaction-bar/types';
import type {Participant} from 'shells/timeline/types';

import type {Props as TimelineEntryContentProps} from '../entry/timeline-entry-content';

import {TimelineEntryModalContent} from './timeline-entry-modal-content';
import {TimelineModalWrapper} from './timeline-modal-wrapper';

import styles from './timeline-entry-modal.module.css';

type Props = {
    // Modal props
    isOpen: boolean;
    onClose: () => void;
    currentUserId?: string;
    iconVariant: TimelineEntryIconVariant;
    participantsForAvatarList?: Participant[] | null | undefined;
    actionMenu?: React.ReactNode;
    additionalToolbarOptions?: ToolbarOption[];
    isLoading?: boolean;
    onToggleReaction?: (
        changeLogId: string,
        reactionType: Reaction,
        filteredReactions: Reactions,
        parentChangeLogId?: string
    ) => any;
    filteredEntryReactions?: Reactions;
    changeLogId: string;
    disableReactions?: boolean;
    disableComments?: boolean;
    htmlContent?: string;
    subtitleComponent?: React.ReactNode;
} & TimelineHeaderProps & // Header props // Content props
    TimelineEntryContentProps & // Timeline footer bar props
    TimelineEntryFooterProps & // Comments props
    TimelineCommentsProps;

export function TimelineEntryModal(props: Props) {
    const {
        isOpen,
        isLoading,
        onClose,
        iconVariant,
        participantsForAvatarList,
        timestamp,
        actor,
        actorEmailAddress,
        participants,
        relatedLeads,
        entity,
        entryType,
        changeType,
        subject,
        subjectTitle,
        content,
        htmlContent,
        status,
        filteredEntryReactions,
        changeLogId: entryChangeLogId,
        currentUserId,
        onToggleReaction,
        emailEngagement,
        comments,
        onAddComment,
        onDeleteComment,
        actionMenu,
        metadata,
        additionalToolbarOptions,
        disableReactions,
        disableComments,
        maxHeight,
        numVisits,
        subtitleComponent,
    } = props;

    const [hasModalMounted, setHasModalMounted] = React.useState<boolean>(false);
    const [isCommentsView, setIsCommentsView] = React.useState<boolean>(false);

    const modalContentRef = React.useRef();
    const showHideButtonRef = React.useRef();

    // Not ideal, due to how the modal mounts with a portal, we need to wait for the component
    // to mount before attempting to render the modal content as the content relies on the sizing
    // of the modal to useLayout effects properly.
    React.useEffect(() => {
        setHasModalMounted(true);
    }, []);

    React.useEffect(() => {
        if (hasModalMounted) {
            // Add event listener to show hide button
            if (showHideButtonRef && showHideButtonRef.current) {
                // @ts-expect-error
                showHideButtonRef.current.addEventListener('click', () => {
                    // @ts-expect-error
                    setIsCommentsView(!showHideButtonRef.current.innerText.includes('Hide'));
                });
            }
        }
    }, [hasModalMounted, comments]);

    if (!isOpen) {
        return null;
    }

    return (
        <TimelineModalWrapper
            isLoading={isLoading}
            onClose={onClose}
            iconVariant={iconVariant}
            timestamp={timestamp}
            actor={actor}
            actorEmailAddress={actorEmailAddress}
            participants={participants}
            entity={entity}
            entryType={entryType}
            changeType={changeType}
            numVisits={numVisits}
            subtitleComponent={subtitleComponent}
        >
            {/* @ts-expect-error */}
            <div ref={modalContentRef}>
                {isCommentsView ? undefined : ( // We hide entry content when viewing all comments
                    <div className={styles['entry-content']}>
                        <TimelineEntryModalContent
                            changeLogEntryId={entryChangeLogId}
                            subject={subject}
                            subjectTitle={subjectTitle}
                            content={content}
                            htmlContent={htmlContent}
                            status={status}
                            participants={participantsForAvatarList}
                            relatedLeads={relatedLeads}
                            actionMenu={actionMenu}
                            additionalToolbarOptions={additionalToolbarOptions}
                            metadata={metadata}
                            maxHeight={maxHeight}
                        />
                        <TimelineEntryFooter
                            reactions={filteredEntryReactions}
                            disableReactions={disableReactions}
                            currentUserId={currentUserId}
                            onToggleReaction={
                                onToggleReaction && filteredEntryReactions
                                    ? (reactionType: Reaction) => {
                                          onToggleReaction(
                                              entryChangeLogId,
                                              reactionType,
                                              filteredEntryReactions
                                          );
                                      }
                                    : undefined
                            }
                            emailEngagement={emailEngagement}
                            alwaysShowAddButton={true}
                        />
                    </div>
                )}
                {!disableComments ? (
                    <TimelineComments
                        comments={comments}
                        currentUserId={currentUserId}
                        onToggleReaction={
                            onToggleReaction
                                ? (
                                      changeLogId: string,
                                      reactionType: Reaction,
                                      commentReactions: Reactions
                                  ) =>
                                      onToggleReaction(
                                          changeLogId,
                                          reactionType,
                                          commentReactions,
                                          entryChangeLogId
                                      )
                                : undefined
                        }
                        onAddComment={onAddComment}
                        onDeleteComment={onDeleteComment}
                        alwaysShowAddComment={true}
                        maxNumShown={1}
                        // @ts-expect-error
                        getShowHideButtonRef={(ref) => {
                            showHideButtonRef.current = ref;
                        }}
                        commentSectionMaxHeight={isCommentsView ? 500 : undefined}
                        limitCommentHeight={!isCommentsView}
                    />
                ) : undefined}
            </div>
        </TimelineModalWrapper>
    );
}
